import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Globe2, Loader2, Sparkles, Crown, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const COLOR_PRESETS = [
  { id: 'nebula',  label: 'Nebula',  value: 'from-fuchsia-500 to-cyan-500' },
  { id: 'sunset',  label: 'Sunset',  value: 'from-amber-500 to-rose-500' },
  { id: 'forest',  label: 'Forest',  value: 'from-emerald-500 to-teal-600' },
  { id: 'ocean',   label: 'Ocean',   value: 'from-sky-500 to-indigo-600' },
  { id: 'aurora',  label: 'Aurora',  value: 'from-violet-500 to-fuchsia-600' },
  { id: 'ember',   label: 'Ember',   value: 'from-orange-500 to-rose-600' },
];

const EMOJI_PRESETS = ['🌌','🪐','🌟','🌠','✨','🚀','👾','🎮','🎧','💜','🌊','🔥','🍜','🎨'];

const CATEGORY_TAGS = ['Music','Gaming','Anime','Tech','Dating','Food','Space','Chill','Welcome'];

/**
 * CreatePlanetDialog — rank-locked planet creation.
 * Reads /planets/mine/limits on open and prevents submit when the cap is reached,
 * showing an upgrade nudge instead. On 403 from the server, surfaces the same
 * message via toast (belt-and-braces).
 */
export default function CreatePlanetDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const [limits, setLimits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    emoji: '🌌',
    color: COLOR_PRESETS[0].value,
    tags: ['Chill'],
  });

  useEffect(() => {
    if (!open) return;
    setForm({ name: '', description: '', emoji: '🌌', color: COLOR_PRESETS[0].value, tags: ['Chill'] });
    api.get('/planets/mine/limits').then(r => setLimits(r.data)).catch(() => setLimits(null));
  }, [open]);

  const atCap = limits && !limits.unlimited && limits.remaining <= 0;

  const submit = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('Planet name must be at least 2 characters.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/planets', {
        name: form.name.trim(),
        description: form.description.trim() || 'A cosmic corner of WorldVibe.',
        emoji: form.emoji,
        color: form.color,
        tags: form.tags,
      });
      toast.success(`Planet "${data.name}" launched 🌌`);
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not create planet.');
      if (e?.response?.status === 403) {
        // Refresh limits so the UI shows the cap state
        api.get('/planets/mine/limits').then(r => setLimits(r.data)).catch(() => {});
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleTag = (t) => {
    setForm(f => ({
      ...f,
      tags: f.tags.includes(t) ? f.tags.filter(x => x !== t) : [...f.tags, t].slice(0, 3),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#161622] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="create-planet-dialog"
      >
        <DialogTitle className="flex items-center gap-2 font-display text-xl">
          <Globe2 size={18} className="text-fuchsia-300" /> Launch a new Planet
        </DialogTitle>
        <DialogDescription className="text-white/50 text-xs">
          Create a themed chat room. Members can join, chat, send gifts and play games together.
        </DialogDescription>

        {/* Rank cap surface */}
        {limits && (
          <div
            className={`rounded-xl border px-3 py-2 flex items-center gap-2 text-xs ${
              atCap
                ? 'border-amber-400/40 bg-amber-500/10 text-amber-200'
                : 'border-white/10 bg-white/5 text-white/70'
            }`}
            data-testid="planet-cap-banner"
          >
            {atCap ? <Lock size={14} className="text-amber-300" /> : <Sparkles size={14} className="text-cyan-300" />}
            <span className="flex-1">
              {limits.unlimited
                ? <>Unlimited planet slots as <span className="font-semibold text-white">{limits.rank_name}</span> 🚀</>
                : <>Planet slots used <span className="font-semibold text-white">{limits.current}/{limits.cap}</span> · rank <span className="font-semibold text-white">{limits.rank_name}</span></>
              }
            </span>
            {atCap && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-400/40 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Crown size={9} /> Rank up
              </span>
            )}
          </div>
        )}

        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Lo-fi Study Cafe"
              maxLength={40}
              disabled={atCap}
              data-testid="create-planet-name"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Description</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What's this planet about?"
              maxLength={160}
              disabled={atCap}
              rows={2}
              data-testid="create-planet-desc"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1 resize-none"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Emoji</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {EMOJI_PRESETS.map(e => (
                <button
                  key={e}
                  onClick={() => setForm({ ...form, emoji: e })}
                  disabled={atCap}
                  data-testid={`emoji-${e}`}
                  className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center border transition ${
                    form.emoji === e
                      ? 'bg-fuchsia-500/20 border-fuchsia-400/50 scale-110'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  } ${atCap ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Color</label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c.id}
                  onClick={() => setForm({ ...form, color: c.value })}
                  disabled={atCap}
                  data-testid={`color-${c.id}`}
                  className={`h-10 rounded-lg bg-gradient-to-br ${c.value} border-2 transition text-[10px] font-semibold text-white/90 uppercase tracking-wider ${
                    form.color === c.value ? 'border-white ring-2 ring-white/40' : 'border-transparent hover:border-white/30'
                  } ${atCap ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-white/50">Tags <span className="text-white/30">(up to 3)</span></label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {CATEGORY_TAGS.map(t => {
                const active = form.tags.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleTag(t)}
                    disabled={atCap}
                    data-testid={`tag-${t.toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-full text-xs border transition ${
                      active
                        ? 'bg-white text-black border-white'
                        : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                    } ${atCap ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="create-planet-cancel"
            className="text-white/70 hover:text-white hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || atCap || form.name.trim().length < 2}
            data-testid="create-planet-submit"
            className={`rounded-full border-0 font-semibold ${
              atCap
                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                : 'bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white'
            }`}
          >
            {busy ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Launching…</>
            ) : atCap ? (
              <><Lock size={14} className="mr-2" /> Cap reached — level up</>
            ) : (
              <><Sparkles size={14} className="mr-2" /> Launch planet</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
